import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const JobListings = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/admin/jobs');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const allJobs = await response.json();
        
        // Handle duplicates by showing only unique job titles with most recent date
        const uniqueJobs = [];
        const jobMap = new Map();
        
        // Group jobs by title
        allJobs.forEach(job => {
          const titleKey = job.job_title.toLowerCase();
          
          if (!jobMap.has(titleKey) || 
              new Date(job.created_at) > new Date(jobMap.get(titleKey).created_at)) {
            jobMap.set(titleKey, job);
          }
        });
        
        // Convert map to array
        jobMap.forEach(job => uniqueJobs.push(job));
        
        // Sort by date (newest first)
        uniqueJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setJobs(uniqueJobs);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to load jobs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleApplyClick = (jobId) => {
    navigate(`/apply/${jobId}`);
  };

  const formatJobDescription = (description) => {
    if (!description) return null;
    
    // Limit to first 100 characters
    return description.length > 100 
      ? `${description.substring(0, 100)}...` 
      : description;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Try Again  
            </button>
            </div>
      </div>
    );
  }

    return (
    <div className="bg-gray-100 min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Open Positions at Acespire Consulting
          </h1>
          <p className="text-gray-600">
            Explore our current opportunities and take the next step in your career
          </p>
        </div>
        
        {jobs.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <p>No open positions at the moment. Please check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map(job => (
              <div 
                key={job.id} 
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2 line-clamp-2 h-14">
                    {job.job_title}
                  </h2>
                  
                  <div className="mb-4 text-sm text-gray-500">
                    <p>Posted: {new Date(job.created_at).toLocaleDateString()}</p>
                    {job.experience_required && (
                      <p className="mt-1">Experience: {job.experience_required}</p>
                    )}
                  </div>
                  
                  <div className="mb-6 h-24 overflow-hidden">
                    {job.full_job_description ? (
                      <p className="text-gray-600 line-clamp-3">
                        {formatJobDescription(job.full_job_description)}
                      </p>
                    ) : job.required_skills && job.required_skills.length > 0 ? (
                      <div>
                        <p className="text-gray-600 font-medium mb-1">Required Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {job.required_skills.map((skill, index) => (
                            <span 
                              key={index}
                              className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">No detailed description available</p>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleApplyClick(job.id)}
                    className="w-full bg-[#275F97] hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobListings;